import { Box, Pressable, useTheme, VStack } from 'native-base';
import { groupBy, map, uniq } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useAppLocale } from '@pimm/common';
import { CleaningScheduleTypeEnum, CleaningTaskStatusEnum } from '@pimm/services/lib/sms-workforce/types';
import { Text, UserCircleIcon } from '@pimm/base';
import { CleaningPlan } from '../reducers';
import { getScheduleColor } from '../utils';

type CleaningCategoryNavProps = {
  plan?: CleaningPlan;
  scheduleType: CleaningScheduleTypeEnum;
  activeCategory?: string | null;
  onCategoryChange?: (category: string) => void;
};

export const CleaningCategoryNav = ({ plan, scheduleType, activeCategory, onCategoryChange }: CleaningCategoryNavProps) => {
  const { translate } = useAppLocale();
  const { t } = useTranslation();
  const { colors } = useTheme();

  if (!plan) return null;

  const scheduleTypeColor = colors[getScheduleColor(scheduleType)];

  let categories = uniq(map(plan.tasks, _ => _.category).filter(Boolean)) as string[];
  // If daily tasks, making sure to show a default categories if empty
  if (categories.length === 0 && plan.scheduleType === 'Daily') {
    categories = ['Daypart 1-3', 'Daypart 4-6'];
  }

  const tasksByCategory = groupBy(plan.tasks, _ => _.category);

  const handleScrollToCategory = (category: string) => {
    // Dispatch custom event to trigger scroll in the table
    const event = new CustomEvent('scrollToCategory', { detail: { category } });
    window.dispatchEvent(event);
    onCategoryChange?.(category);
  };

  return (
    <VStack h="full">
      <Box px={3} pt={2.5} pb={1.5}>
        <Text size="md" fontWeight={600} color="gray.500">
          {t('cleaning_task:task_category')}
        </Text>
      </Box>
      <VStack flex={1}>
        {categories.map(category => {
          const tasks = tasksByCategory[category] ?? [];
          const nonNATasks = tasks.filter(_ => _.assignee?.status !== CleaningTaskStatusEnum.NotApplicable);
          const total = nonNATasks.length;
          const assignedCount = nonNATasks.filter(_ => !!_.assignee?.employeeId).length;
          const allDone = total > 0 && assignedCount === total;
          const partial = assignedCount > 0 && assignedCount < total;
          const isActive = activeCategory === category;

          return (
            <Pressable
              key={category}
              onPress={() => handleScrollToCategory(category)}
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              px={3}
              py={2.5}
              bg={isActive ? scheduleTypeColor[50] : 'transparent'}
              borderLeftWidth={3}
              borderLeftColor={isActive ? scheduleTypeColor[500] : 'transparent'}
              _hover={{ bg: isActive ? scheduleTypeColor[50] : 'gray.50' }}
            >
              <Text size="md" fontWeight={600} color={isActive ? scheduleTypeColor[700] : 'gray.600'} flex={1} lineHeight="sm">
                {translate(category, tasks[0]?.categoryTranslations)}
              </Text>
              {allDone ? (
                <UserCircleIcon color={scheduleTypeColor[500]} />
              ) : (
                <Box
                  px={2}
                  py={0.5}
                  bg={partial ? scheduleTypeColor[50] : 'gray.100'}
                  borderRadius="full"
                  borderWidth={1}
                  borderColor={partial ? scheduleTypeColor[200] : 'gray.300'}
                  flexShrink={0}
                >
                  <Text size="sm" fontWeight={600} color={partial ? scheduleTypeColor[700] : 'gray.500'}>
                    {assignedCount}/{total}
                  </Text>
                </Box>
              )}
            </Pressable>
          );
        })}
      </VStack>
    </VStack>
  );
};
